import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAutomationLogs, clearLogs } from '../../redux/Slices/automationSlice';

const IconClose = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const IconCheck = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 6l2.5 2.5 5-5" />
  </svg>
);

const IconX = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round">
    <path d="M3 3l6 6M9 3l-6 6" />
  </svg>
);

const IconEmpty = ({ size = 40, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
    <rect x="6" y="10" width="28" height="22" rx="3" />
    <path d="M6 18h28" />
    <path d="M14 25h12" />
    <path d="M17 29h6" />
  </svg>
);

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  const month = d.toLocaleString('default', { month: 'short' });
  const day = d.getDate();
  const time = d.toLocaleString('default', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${month} ${day}, ${time}`;
};

const formatActionType = (type) => type?.replace(/_/g, ' ') || 'unknown';

const AutomationLogs = ({ boardId, automationId, automationName, onClose }) => {
  const dispatch = useDispatch();
  const { logs, logsLoading } = useSelector((state) => state.automations);

  useEffect(() => {
    dispatch(fetchAutomationLogs({ boardId, automationId, page: 1, limit: 20 }));
    return () => dispatch(clearLogs());
  }, [boardId, automationId, dispatch]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const totalSuccess = logs.logs?.reduce((sum, log) =>
    sum + (log.actionsExecuted?.filter(a => a.status === 'success').length || 0), 0) || 0;
  const totalFailed = logs.logs?.reduce((sum, log) =>
    sum + (log.actionsExecuted?.filter(a => a.status === 'failed').length || 0), 0) || 0;

  return createPortal(
    <div className="automation-logs-overlay" onClick={onClose}>
      <div className="automation-logs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="logs-modal-header">
          <div className="logs-modal-title">
            <h3>Execution History</h3>
            {automationName && <span className="logs-rule-name">{automationName}</span>}
          </div>
          <button className="logs-close-btn" onClick={onClose}>
            <IconClose />
          </button>
        </div>

        {!logsLoading && logs.logs?.length > 0 && (
          <div className="logs-summary-bar">
            <span className="summary-item">
              <strong>{logs.logs.length}</strong> executions
            </span>
            {totalSuccess > 0 && (
              <span className="summary-item success">
                <IconCheck /> {totalSuccess} passed
              </span>
            )}
            {totalFailed > 0 && (
              <span className="summary-item failed">
                <IconX /> {totalFailed} failed
              </span>
            )}
          </div>
        )}

        <div className="logs-modal-body">
          {logsLoading ? (
            <div className="logs-modal-empty">Loading...</div>
          ) : !logs.logs?.length ? (
            <div className="logs-modal-empty">
              <IconEmpty />
              <span>No executions yet</span>
            </div>
          ) : (
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Card</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.logs.map((log, index) => (
                  <tr key={log._id || index}>
                    <td className="log-cell-time">{formatTime(log.executedAt)}</td>
                    <td className="log-cell-card">
                      {log.triggerContext?.cardTitle || (log.triggerContext?.cardCount ? `${log.triggerContext.cardCount} cards` : '—')}
                    </td>
                    <td className="log-cell-actions">
                      {log.actionsExecuted?.map((action, i) => (
                        <span key={i} className={`log-pill ${action.status}`}>
                          {action.status === 'success' ? <IconCheck /> : <IconX />}
                          {formatActionType(action.type)}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {logs.totalPages > 1 && (
          <div className="logs-modal-footer">
            Page {logs.page} of {logs.totalPages}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AutomationLogs;
